import React, { useState } from 'react';
import {
    Save,
    Sparkles,
    Upload,
    ChevronDown,
    RotateCcw,
    Trash2,
    FileDown,
    Copy,
    LayoutGrid,
    CheckCircle,
    AlertCircle,
    HelpCircle,
    Pencil,
    Check,
    X
} from 'lucide-react';
import { useFunnelBuilder } from '../../contexts/FunnelBuilderContext';
import { useApp } from '../../contexts/AppContext';
import ConfirmationModal from '../ConfirmationModal';

/**
 * FunnelToolbar Component
 * Top toolbar with save, generate, push, and utility actions
 */
const FunnelToolbar = ({ onAutoLayout, onOpenHelp }) => {
    const {
        currentFunnel,
        nodes,
        edges,
        totalBudget,
        saveFunnel,
        clearFunnel,
        renameFunnel,
        updateNodes,
        isSaving,
        isDirty,
        activePlatform
    } = useFunnelBuilder();

    const { selectedBrand } = useApp();

    const [showPushMenu, setShowPushMenu] = useState(false);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generateResult, setGenerateResult] = useState(null);
    const [isPushing, setIsPushing] = useState(false);
    const [saveResult, setSaveResult] = useState(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');

    const campaignCount = nodes.filter(n => n.type === 'campaign').length;
    const adSetCount = nodes.filter(n => n.type === 'adset').length;
    const adCount = nodes.filter(n => n.type === 'ad').length;

    const handleSave = async () => {
        setSaveResult(null);
        try {
            await saveFunnel();
            setSaveResult({ success: true });
            setTimeout(() => setSaveResult(null), 3000);
        } catch (error) {
            console.error('Error saving funnel:', error);
            setSaveResult({ error: error.message });
            setTimeout(() => setSaveResult(null), 5000);
        }
    };

    // Find parent node by traversing edges backwards
    const findParentNode = (nodeId, parentType) => {
        // Find edge where this node is the target
        const edge = edges.find(e => e.target === nodeId);
        if (!edge) return null;

        const parentNode = nodes.find(n => n.id === edge.source);
        if (!parentNode) return null;

        if (parentNode.type === parentType) {
            return parentNode;
        }

        // Keep traversing up
        return findParentNode(parentNode.id, parentType);
    };

    const handleGenerateAll = async () => {
        setIsGenerating(true);
        setGenerateResult(null);

        try {
            // Get all ad nodes
            const adNodes = nodes.filter(n => n.type === 'ad');

            if (adNodes.length === 0) {
                console.log('No ads to generate');
                return;
            }

            // Build the request payload with context for each ad
            const adsWithContext = adNodes.map(ad => {
                // Find parent ad set
                const adSet = findParentNode(ad.id, 'adset');
                // Find parent campaign (either direct or through ad set)
                const campaign = adSet
                    ? findParentNode(adSet.id, 'campaign')
                    : findParentNode(ad.id, 'campaign');

                return {
                    id: ad.id,
                    campaign: campaign?.data || null,
                    adSet: adSet?.data || null
                };
            });

            console.log('Generating ad content for', adsWithContext.length, 'ads...');

            // Call the API
            const response = await fetch('http://localhost:3001/api/funnels/generate-ads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ads: adsWithContext,
                    brand: selectedBrand,
                    platform: activePlatform
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate ads');
            }

            const result = await response.json();

            // Update nodes with generated content
            const nodeUpdates = result.generatedAds
                .filter(ad => ad.success)
                .map(ad => ({
                    id: ad.id,
                    data: {
                        headline: ad.headline,
                        primaryText: ad.primaryText,
                        description: ad.description,
                        cta: ad.cta,
                        variations: ad.variations,
                        status: 'generated',
                        generatedAt: ad.generatedAt
                    }
                }));

            if (nodeUpdates.length > 0) {
                updateNodes(nodeUpdates);
            }

            // Show result feedback
            setGenerateResult({
                success: result.summary.success,
                failed: result.summary.failed,
                total: result.summary.total
            });

            // Clear result after 5 seconds
            setTimeout(() => setGenerateResult(null), 5000);

            console.log(`Generated ${result.summary.success}/${result.summary.total} ads`);
        } catch (error) {
            console.error('Error generating content:', error);
            setGenerateResult({ error: error.message });
            setTimeout(() => setGenerateResult(null), 5000);
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePush = async (platform) => {
        setShowPushMenu(false);
        setIsPushing(true);
        try {
            // TODO: Push funnel to ad platform API
            console.log(`Pushing funnel to ${platform}...`);
            await new Promise(resolve => setTimeout(resolve, 2000)); // Placeholder
        } catch (error) {
            console.error('Error pushing to platform:', error);
        } finally {
            setIsPushing(false);
        }
    };

    const handleClear = () => {
        clearFunnel();
        setShowClearConfirm(false);
    };

    const handleStartRename = () => {
        setEditedName(currentFunnel.name || '');
        setIsEditingName(true);
    };

    const handleConfirmRename = () => {
        const trimmedName = editedName.trim();
        if (trimmedName) {
            renameFunnel(trimmedName);
        }
        setIsEditingName(false);
    };

    const handleCancelRename = () => {
        setIsEditingName(false);
        setEditedName('');
    };

    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleConfirmRename();
        } else if (e.key === 'Escape') {
            handleCancelRename();
        }
    };

    const formatBudget = (amount) => {
        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    };

    const getPlatformLabel = () => {
        const labels = {
            meta: 'Meta Ads',
            google: 'Google Ads',
            tiktok: 'TikTok Ads',
            linkedin: 'LinkedIn Ads'
        };
        return labels[activePlatform] || activePlatform;
    };

    return (
        <>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--color-surface)',
                borderBottom: '1px solid var(--color-border)',
                gap: 'var(--space-4)'
            }}>
                {/* Left section - Title and stats */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <div>
                        {isEditingName ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <input
                                    type="text"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    onKeyDown={handleRenameKeyDown}
                                    autoFocus
                                    style={{
                                        fontSize: 'var(--font-size-lg)',
                                        fontWeight: 600,
                                        padding: 'var(--space-1) var(--space-2)',
                                        border: '2px solid var(--color-primary)',
                                        borderRadius: 'var(--radius-sm)',
                                        background: 'var(--color-bg-primary)',
                                        color: 'var(--color-text-primary)',
                                        outline: 'none',
                                        minWidth: '200px'
                                    }}
                                    placeholder="Enter funnel name"
                                />
                                <button
                                    onClick={handleConfirmRename}
                                    style={{
                                        padding: 'var(--space-1)',
                                        background: 'var(--color-success)',
                                        border: 'none',
                                        borderRadius: 'var(--radius-sm)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white'
                                    }}
                                    title="Confirm"
                                >
                                    <Check size={16} />
                                </button>
                                <button
                                    onClick={handleCancelRename}
                                    style={{
                                        padding: 'var(--space-1)',
                                        background: 'var(--color-bg-tertiary)',
                                        border: 'none',
                                        borderRadius: 'var(--radius-sm)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--color-text-secondary)'
                                    }}
                                    title="Cancel"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <h2
                                    onClick={handleStartRename}
                                    style={{
                                        margin: 0,
                                        fontSize: 'var(--font-size-lg)',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        padding: 'var(--space-1) var(--space-2)',
                                        borderRadius: 'var(--radius-sm)',
                                        transition: 'background 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-tertiary)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                    title="Click to rename"
                                >
                                    {currentFunnel.name || 'Untitled Funnel'}
                                </h2>
                                <button
                                    onClick={handleStartRename}
                                    style={{
                                        padding: 'var(--space-1)',
                                        background: 'transparent',
                                        border: 'none',
                                        borderRadius: 'var(--radius-sm)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--color-text-tertiary)',
                                        transition: 'color 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'var(--color-text-primary)'}
                                    onMouseLeave={(e) => e.target.style.color = 'var(--color-text-tertiary)'}
                                    title="Rename funnel"
                                >
                                    <Pencil size={14} />
                                </button>
                                {isDirty && (
                                    <span style={{
                                        color: 'var(--color-warning)',
                                        fontSize: 'var(--font-size-sm)'
                                    }}>
                                        (unsaved)
                                    </span>
                                )}
                            </div>
                        )}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-3)',
                            marginTop: 'var(--space-1)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            <span>{campaignCount} campaign{campaignCount !== 1 ? 's' : ''}</span>
                            <span>•</span>
                            <span>{adSetCount} ad set{adSetCount !== 1 ? 's' : ''}</span>
                            <span>•</span>
                            <span>{adCount} ad{adCount !== 1 ? 's' : ''}</span>
                        </div>
                    </div>

                    {/* Budget summary */}
                    <div style={{
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            Daily Budget:
                        </span>
                        <span style={{
                            fontWeight: 600,
                            color: 'var(--color-primary)'
                        }}>
                            {formatBudget(totalBudget)}
                        </span>
                    </div>
                </div>

                {/* Right section - Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    {/* Help */}
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={onOpenHelp}
                        title="Get help with building your funnel"
                        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                    >
                        <HelpCircle size={16} />
                        Help
                    </button>

                    {/* Auto Layout */}
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={onAutoLayout}
                        title="Auto-arrange nodes"
                    >
                        <LayoutGrid size={16} />
                        Layout
                    </button>

                    {/* Clear */}
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowClearConfirm(true)}
                        disabled={nodes.length === 0}
                        title="Clear canvas"
                    >
                        <Trash2 size={16} />
                    </button>

                    <div style={{
                        width: '1px',
                        height: '24px',
                        background: 'var(--color-border)',
                        margin: '0 var(--space-2)'
                    }} />

                    {/* Save */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSave}
                            disabled={isSaving || !isDirty}
                        >
                            <Save size={16} />
                            {isSaving ? 'Saving...' : 'Save Draft'}
                        </button>

                        {/* Save result feedback */}
                        {saveResult?.success && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-2)',
                                background: 'rgba(16, 185, 129, 0.1)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-success)'
                            }}>
                                <CheckCircle size={14} />
                                Saved
                            </div>
                        )}
                        {saveResult?.error && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-2)',
                                background: 'rgba(239, 68, 68, 0.1)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-error)'
                            }}>
                                <AlertCircle size={14} />
                                {saveResult.error}
                            </div>
                        )}
                    </div>

                    {/* Generate All */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={handleGenerateAll}
                            disabled={isGenerating || adCount === 0}
                        >
                            <Sparkles size={16} />
                            {isGenerating ? 'Generating...' : 'Generate All'}
                        </button>

                        {/* Generation result feedback */}
                        {generateResult && !generateResult.error && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-2)',
                                background: 'rgba(16, 185, 129, 0.1)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-success)'
                            }}>
                                <CheckCircle size={14} />
                                {generateResult.success}/{generateResult.total} generated
                            </div>
                        )}
                        {generateResult?.error && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-2)',
                                background: 'rgba(239, 68, 68, 0.1)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-error)'
                            }}>
                                <AlertCircle size={14} />
                                {generateResult.error}
                            </div>
                        )}
                    </div>

                    {/* Push to Platform */}
                    <div style={{ position: 'relative' }}>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowPushMenu(!showPushMenu)}
                            disabled={isPushing || nodes.length === 0}
                        >
                            <Upload size={16} />
                            {isPushing ? 'Pushing...' : `Push to ${getPlatformLabel()}`}
                            <ChevronDown size={14} />
                        </button>

                        {showPushMenu && (
                            <>
                                <div
                                    style={{
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 99
                                    }}
                                    onClick={() => setShowPushMenu(false)}
                                />
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: 'var(--space-2)',
                                    background: 'var(--color-surface)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    boxShadow: 'var(--shadow-lg)',
                                    minWidth: '200px',
                                    zIndex: 100,
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        padding: 'var(--space-2)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        borderBottom: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-tertiary)'
                                    }}>
                                        Push funnel to:
                                    </div>
                                    <button
                                        style={{
                                            width: '100%',
                                            padding: 'var(--space-3)',
                                            background: 'none',
                                            border: 'none',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)'
                                        }}
                                        onClick={() => handlePush('meta')}
                                        onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-tertiary)'}
                                        onMouseLeave={(e) => e.target.style.background = 'none'}
                                    >
                                        <span style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: '#1877F2'
                                        }} />
                                        Meta Ads Manager
                                    </button>
                                    <button
                                        style={{
                                            width: '100%',
                                            padding: 'var(--space-3)',
                                            background: 'none',
                                            border: 'none',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)'
                                        }}
                                        onClick={() => handlePush('google')}
                                        onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-tertiary)'}
                                        onMouseLeave={(e) => e.target.style.background = 'none'}
                                    >
                                        <span style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: '#4285F4'
                                        }} />
                                        Google Ads
                                    </button>
                                    <div style={{
                                        borderTop: '1px solid var(--color-border)',
                                        padding: 'var(--space-2)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        Creates campaigns as drafts
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Clear Confirmation Modal */}
            <ConfirmationModal
                isOpen={showClearConfirm}
                onClose={() => setShowClearConfirm(false)}
                onConfirm={handleClear}
                title="Clear Canvas?"
                message="This will remove all campaigns, ad sets, and ads from the canvas. This action cannot be undone."
                confirmText="Clear All"
                cancelText="Cancel"
                variant="danger"
            />
        </>
    );
};

export default FunnelToolbar;
